import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getOrderInfo,
  jsapiCreateOrder,
  queryOrderStatus,
  PayType,
} from "@/api/wechatPay";
import { invokeWxJsapiPay, isWeixin } from "@/utils/wechatPay";
import {
  toastSuccess,
  toastWarning,
  toastInfo,
  toastLoading,
} from "@/utils/toast";

import { PaymentHeader } from "./components/PaymentHeader";
import {
  PaymentMethodList,
  PaymentMethod,
} from "./components/PaymentMethodList";
import { PaymentSummary } from "./components/PaymentSummary";
import { PaymentFooter } from "./components/PaymentFooter";
import { CreditApplyModal } from "./components/CreditApplyModal";

export const BRAND = "#FF6B35";

export type OrderType = "car" | "hotel" | "tour" | "guide";

/** 统一订单详情数据（兼容三种业务线） */
interface UnifiedOrderDetail {
  order_no: string;
  total_amount: number;
  /** 用于 Header 展示的描述文本 */
  description: string;
}

export default function PaymentPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // 从 order-form 页面传入的订单信息
  const routeState = location.state as
    | {
        orderNo?: string;
        totalAmount?: string;
        orderId?: number;      // BLW 达人订单 ID
        type?: string;
        isExpertOrder?: boolean; // 标识是否为达人订单
      }
    | undefined;
  const orderNo = routeState?.orderNo ?? "";
  const orderId = routeState?.orderId;
  // 必须显式传 isExpertOrder: true 才走达人 BLW 支付，避免其他页面传 orderId 被误判
  const isExpertOrder = routeState?.isExpertOrder === true;
  const orderType = (routeState?.type ?? "car") as OrderType;

  const [method, setMethod] = useState<PaymentMethod>("wechat");
  // const [showCreditApply, setShowCreditApply] = useState(false);
  // const [creditApplied, setCreditApplied] = useState(false);
  const [paying, setPaying] = useState(false);
  /** 支付步骤提示文本（用于在 UI 上展示当前进度） */
  const [payStepText, setPayStepText] = useState("");

  // 订单详情
  const [orderDetail, setOrderDetail] = useState<UnifiedOrderDetail | null>(
    null,
  );
  const [loadingDetail, setLoadingDetail] = useState(true);

  const orderAmount =
    orderDetail?.total_amount || Number(routeState?.totalAmount) || 0;
  const total = orderAmount;

  /** 将 orderType 字符串映射为 PayType（后端字符串值） */
  const getPayType = useCallback((): PayType => {
    switch (orderType) {
      case "car":
        return PayType.Car;
      case "hotel":
        return PayType.Hotel;
      case "tour":
        return PayType.Tour;
      case "group_tour":
        return PayType.GroupTour;
      case "guide":
        return PayType.Guide;
      default:
        return PayType.Car;
    }
  }, [orderType]);

  // 用统一接口获取订单详情（达人订单跳过，直接用路由数据）
  useEffect(() => {
    if (isExpertOrder) {
      // 达人订单：直接从路由 state 取数据
      setOrderDetail({
        order_no: `BLW-${orderId}`,
        total_amount: Number(routeState?.totalAmount) || 0,
        description: "达人服务订单",
      });
      setLoadingDetail(false);
      return;
    }

    if (!orderNo) {
      setLoadingDetail(false);
      return;
    }

    let cancelled = false;

    getOrderInfo(orderNo, getPayType())
      .then((res) => {
        if (cancelled) return;
        if (res.data.code === 200 && res.data.data) {
          const d = res.data.data;
          setOrderDetail({
            order_no: d.order_no,
            total_amount: Number(d.amount),
            description: d.title,
          });
        }
      })
      .catch((err) => console.error("[Payment] 获取订单信息失败:", err))
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderNo, orderType, getPayType]);

  /** 支付主流程：统一走主系统 /home/wechat-pay/jsapi/pay */
  const handlePay = async () => {
    console.log("[Payment] === handlePay 开始 ===");
    console.log("[Payment] isExpertOrder:", isExpertOrder, "orderId:", orderId, "orderNo:", orderNo);

    // 微信环境检测（开发环境下跳过检测以便调试）
    const isDev = import.meta.env.DEV;
    if (!isWeixin() && !isDev) {
      toastInfo("请在微信中打开此页面以完成支付");
      console.warn("[Payment] 非微信环境，已拦截");
      return;
    }
    if (!isWeixin() && isDev) {
      console.log("[Payment] 开发模式：跳过微信环境检测（仅用于调试）");
    }

    if (isExpertOrder && !orderId) {
      toastWarning("缺少达人订单ID");
      return;
    }
    if (!isExpertOrder && !orderNo) {
      toastWarning("缺少订单号");
      return;
    }
    if (paying) {
      toastWarning("支付处理中，请稍候");
      return;
    }

    setPaying(true);
    setPayStepText("正在准备支付...");
    const loadingToast = toastLoading("正在发起支付...");

    // ── 统一错误处理 ─────────────────────────────────
    const cleanup = (msg?: string) => {
      loadingToast.close();
      setPaying(false);
      setPayStepText("");
      if (msg) toastWarning(msg);
    };

    try {
      // ========== 步骤1: 获取订单号 & 确定支付类型 ==========
      const payOrderNo = isExpertOrder ? orderNo : orderNo;
      // 达人(expert)订单后端不支持 expert 类型，映射为 guide
      const payTypeValue = isExpertOrder ? PayType.Guide : getPayType();

      if (!payOrderNo) {
        cleanup(isExpertOrder ? "达人订单缺少订单号" : "缺少订单号");
        return;
      }

      console.log("[Payment] 步骤1: 调用 jsapiCreateOrder, orderNo:", payOrderNo, "type:", payTypeValue);
      setPayStepText("正在创建支付订单...");

      let payRes;
      try {
        payRes = await jsapiCreateOrder(payOrderNo, payTypeValue);
        console.log("[Payment] 步骤1成功: jsapiCreateOrder 返回:", JSON.stringify(payRes?.data));
      } catch (apiErr: any) {
        console.error("[Payment] 步骤1失败: jsapiCreateOrder 异常:", apiErr);
        const errMsg = apiErr?.response?.data?.message
          || apiErr?.response?.data?.msg
          || apiErr?.message
          || "网络异常，创建支付订单失败";
        cleanup(errMsg);
        return;
      }

      // 防御性检查
      if (!payRes?.data || payRes.data.code !== 200 || !payRes.data.data) {
        console.error("[Payment] 步骤2失败: 支付接口返回异常, 响应:", JSON.stringify(payRes?.data));
        cleanup(payRes?.data?.message || "支付接口返回异常，请重试");
        return;
      }

      loadingToast.close();
      setPayStepText("正在调起微信支付...");

      // ========== 步骤2: 调起微信收银台 ==========
      const jsConfig = payRes.data.data;
      console.log("[Payment] 步骤2: jsConfig =", { ...jsConfig, paySign: "***" });

      // 开发环境下跳过实际调起微信收银台
      if (isDev && !isWeixin()) {
        console.log("[Payment] 开发模式：模拟支付成功");
        cleanup();
        toastSuccess("开发模式：模拟支付成功！");
        navigate("/orders", { replace: true });
        return;
      }

      // 检查 jsConfig 必要字段
      if (!jsConfig.appId || !jsConfig.package || !jsConfig.paySign) {
        console.error("[Payment] 步骤2失败: jsConfig 缺少必要字段:", jsConfig);
        cleanup("支付参数不完整，请重新下单");
        return;
      }

      let wxPaySucceeded = false;
      try {
        await invokeWxJsapiPay(jsConfig);
        wxPaySucceeded = true;
        console.log("[Payment] 步骤3: 微信支付回调成功");
      } catch (wxErr: any) {
        console.error("[Payment] 步骤3失败: 微信支付异常:", wxErr);
        cleanup(wxErr?.errMsg || "微信支付失败，请重试");
        return;
      }

      // ========== 步骤3: 查询支付结果 ==========
      setPayStepText("正在确认支付结果...");
      try {
        const queryRes = await queryOrderStatus(payOrderNo, payTypeValue);
        if (queryRes?.data?.code === 200 && queryRes.data.data) {
          toastSuccess("支付成功！");
        } else {
          toastSuccess("支付已完成，请确认订单状态");
        }
      } catch (queryErr) {
        console.warn("[Payment] queryOrderStatus 失败(不影响跳转):", queryErr);
        toastSuccess("支付完成，请查看订单状态");
      }

      cleanup();
      navigate("/orders", { replace: true });
    } catch (err: any) {
      console.error("[Payment Error] 未捕获异常:", err);
      const displayMsg = err?.errMsg || err?.message || "支付异常，请重试";
      cleanup(displayMsg);
    }
  };

  return (
    <div
      className="min-h-screen pb-[calc(env(safe-area-inset-bottom,0px)+144px)]"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      <PaymentHeader
        orderAmount={orderAmount}
        depositAmount={0}
        total={total}
        description={orderDetail?.description}
        loading={loadingDetail}
      />

      <div className="px-4 py-4 space-y-3">
        {/* 支付步骤进度提示 */}
        {paying && payStepText && (
          <div className="flex items-center justify-center gap-2 py-2 rounded-lg"
            style={{ background: "#FFF7ED", color: "#C2410C" }}
          >
            <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-medium">{payStepText}</span>
          </div>
        )}

        <PaymentMethodList method={method} onMethodChange={setMethod} />
        <PaymentSummary
          orderAmount={orderAmount}
          depositAmount={0}
          isCreditApplied={false}
          loading={loadingDetail}
        />
      </div>

      <PaymentFooter
        total={total}
        orderAmount={orderAmount}
        onPay={handlePay}
        paying={paying}
      />

      {/* {showCreditApply && (
        <CreditApplyModal
          creditGranted={creditGranted}
          depositAmount={depositAmount}
          onConfirm={() => {
            setCreditApplied(true);
            setMethod("credit_wechat");
            setShowCreditApply(false);
          }}
          onClose={() => setShowCreditApply(false)}
        />
      )} */}
    </div>
  );
}
